import { authHelper } from '../utils/authHelper';
import React, { useState, useEffect } from 'react';
import { Save, Loader2, CheckCircle2, XCircle, Key, Mail, Globe } from 'lucide-react';

export function AdminConfigPanel() {
  const [config, setConfig] = useState({
    stripe_publishable_key: '',
    stripe_secret_key: '',
    sender_email: '',
    resend_api_key: '',
    app_url: '',
    trial_days: 7
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/config`, {
        headers: {
          'Authorization': `Bearer ${authHelper.getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authHelper.getToken()}`
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Configuración guardada exitosamente' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        throw new Error('Error al guardar configuración');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A14C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Configuración del Sistema</h2>
          <p className="text-white/60">Gestiona Stripe, emails y ajustes generales</p>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#C5A14C] text-[#02040A] font-bold rounded-full px-6 py-3 hover:bg-[#B08F3C] transition-all duration-300 flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Guardar Cambios
            </>
          )}
        </button>
      </div>

      {message.text && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' 
            ? 'bg-emerald-500/10 border border-emerald-500/20' 
            : 'bg-red-500/10 border border-red-500/20'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
          <p className={message.type === 'success' ? 'text-emerald-400' : 'text-red-400'}>
            {message.text}
          </p>
        </div>
      )}

      {/* Stripe Configuration */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10">
        <div className="flex items-center gap-2 mb-6">
          <Key className="w-5 h-5 text-[#C5A14C]" />
          <h3 className="text-xl font-bold text-white">Configuración de Stripe</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-white/60 mb-2">Publishable Key (Pública)</label>
            <input
              type="text"
              value={config.stripe_publishable_key}
              onChange={(e) => setConfig({ ...config, stripe_publishable_key: e.target.value })}
              placeholder="pk_live_..."
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#C5A14C] font-mono text-sm"
            />
            <p className="text-xs text-white/40 mt-1">Obtén esta key desde tu dashboard de Stripe</p>
          </div>

          <div>
            <label className="block text-sm text-white/60 mb-2">Secret Key (Secreta)</label>
            <input
              type="password"
              value={config.stripe_secret_key}
              onChange={(e) => setConfig({ ...config, stripe_secret_key: e.target.value })}
              placeholder="sk_live_..."
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#C5A14C] font-mono text-sm"
            />
            <p className="text-xs text-white/40 mt-1">⚠️ Mantén esta key segura y nunca la compartas</p>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-amber-500 mt-0.5" />
              <div className="text-sm text-amber-200">
                <p className="font-semibold mb-1">Cómo obtener tus keys de Stripe:</p>
                <ol className="list-decimal list-inside space-y-1 text-amber-200/80">
                  <li>Ve a https://dashboard.stripe.com</li>
                  <li>Inicia sesión o crea una cuenta</li>
                  <li>Ve a Developers → API keys</li>
                  <li>Copia las keys y pégalas aquí</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Configuration */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10">
        <div className="flex items-center gap-2 mb-6">
          <Mail className="w-5 h-5 text-[#C5A14C]" />
          <h3 className="text-xl font-bold text-white">Configuración de Email</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-white/60 mb-2">Email Remitente</label>
            <input
              type="email"
              value={config.sender_email}
              onChange={(e) => setConfig({ ...config, sender_email: e.target.value })}
              placeholder="noreply@metodo11.com"
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#C5A14C]"
            />
          </div>

          <div>
            <label className="block text-sm text-white/60 mb-2">Resend API Key (Opcional)</label>
            <input
              type="password"
              value={config.resend_api_key}
              onChange={(e) => setConfig({ ...config, resend_api_key: e.target.value })}
              placeholder="re_..."
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#C5A14C] font-mono text-sm"
            />
            <p className="text-xs text-white/40 mt-1">Para enviar emails transaccionales (recuperación de contraseña, etc.)</p>
          </div>
        </div>
      </div>

      {/* General Settings */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10">
        <div className="flex items-center gap-2 mb-6">
          <Globe className="w-5 h-5 text-[#C5A14C]" />
          <h3 className="text-xl font-bold text-white">Ajustes Generales</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-white/60 mb-2">URL de la Aplicación</label>
            <input
              type="url"
              value={config.app_url}
              onChange={(e) => setConfig({ ...config, app_url: e.target.value })}
              placeholder="https://metodo11.com"
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#C5A14C]"
            />
          </div>

          <div>
            <label className="block text-sm text-white/60 mb-2">Días de Período de Prueba</label>
            <input
              type="number"
              value={config.trial_days}
              onChange={(e) => setConfig({ ...config, trial_days: parseInt(e.target.value) })}
              min="1"
              max="30"
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#C5A14C]"
            />
            <p className="text-xs text-white/40 mt-1">Días de acceso gratuito para nuevos usuarios (actualmente: {config.trial_days} días)</p>
          </div>
        </div>
      </div>
    </div>
  );
}
